import { useState } from 'react';
import { Flex, Text } from '@nula/core-ui';
import styles from './confirm-menu-item.module.css';

interface ConfirmMenuItemProps {
  /** Resting label (e.g. "Delete"). */
  label: React.ReactNode;
  /** Armed label shown after the first click (e.g. "Click to confirm"). */
  confirmLabel: React.ReactNode;
  /** Fired only on the second (confirming) click. */
  onConfirm: () => void;
}

/**
 * A two-step destructive menu-item label. First click arms it (swaps to the
 * confirm label, styled as danger); the second click fires `onConfirm`. Rendered
 * as the `label` of a Dropdown menu item. Reset is by remount — the parent
 * re-keys / remounts the menu on reopen, so a fresh mount always starts disarmed.
 */
export function ConfirmMenuItem({ label, confirmLabel, onConfirm }: ConfirmMenuItemProps) {
  const [armed, setArmed] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    // Stop the click reaching the wrapping Dropdown menu-item <button>, whose
    // onClick would otherwise close the menu (via the Dropdown's onSelect
    // fallback) before the two-step confirm can arm/fire.
    e.stopPropagation();
    if (armed) {
      onConfirm();
    } else {
      setArmed(true);
    }
  };

  return (
    <Flex
      align="center"
      className={armed ? styles.armed : styles.resting}
      onClick={handleClick}
    >
      <Text className={styles.label}>{armed ? confirmLabel : label}</Text>
    </Flex>
  );
}
